import { FluentThemeProvider } from '@azure/communication-react';
import React from 'react';
import { createUseStyles } from 'react-jss';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Text, Stack, StackItem } from '@fluentui/react';
import classNames from 'classnames';
import { ChatPage } from './ChatWindow';
import { Header, defaultHeaderHeight } from './Components/Header';
import { ChatManagerContextProvider } from './Components/ChatManagerContext';
import { DailyPlannerContextProvider } from './Components/DailyPlanner/DailyPlannerContext';
import { PlaygroundTheme } from './Theme';
import { useIsVivara } from './hooks/useIsVivara';
import { useLLMSetting } from './Components/Contexts';
import { Topic } from './Components/DeveloperPanel';

const contentMarginTop = 15;

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  '@global': {
    'html, body': {
      height: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
    },
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.background,
  },
  calloutText: {
    textAlign: 'center',
    display: 'block',
    fontSize: '20px',
  },
  contentContainer: {
    height: `calc(100vh - ${defaultHeaderHeight}px - ${contentMarginTop}px)`,
    flexBasis: '65%',
    flexGrow: 0,
    flexShrink: 1,
    '@media (width <= 600px)': {
      flexBasis: '95%',
    },
    '&.is-vivara': {
      backgroundColor: '#fff',
      borderRadius: 12,
      boxShadow: `0px 32px 64px 0px ${theme.themePrimary}7A`,
      marginTop: 0,

      '&.topicSelection': {
        backgroundColor: 'unset',
        boxShadow: 'unset',
        justifyContent: 'unset',
      },
    },
  },
}));

const stackStyles = {
  root: {
    marginTop: `${contentMarginTop}px`,
  },
};

export const DefaultMessageThreadExample: () => JSX.Element = () => {
  const classes = useStyles();
  const isVivara = useIsVivara();
  const { selectedTopic } = useLLMSetting();

  return (
    <FluentThemeProvider fluentTheme={isVivara ? { defaultFontStyle: { fontFamily: 'aptos-serif' } } : {}}>
      <DailyPlannerContextProvider>
        <ChatManagerContextProvider>
          <div className={classes.container}>
            <Header />
            <Stack horizontal styles={{ root: { width: '100%' } }} horizontalAlign="center">
              <StackItem basis="12px" grow={0} shrink={0}>
                <div />
              </StackItem>
              <Stack
                verticalAlign="center"
                horizontalAlign="stretch"
                grow={1}
                // eslint-disable-next-line max-len
                className={classNames(classes.contentContainer, 'content-container', isVivara && 'is-vivara', isVivara && (!selectedTopic || selectedTopic === Topic.None) && 'topicSelection')}
                styles={stackStyles}
              >
                <AuthenticatedTemplate>
                  <ChatPage />
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <Text className={classes.calloutText} variant="large">You need to sign in to access this application</Text>
                </UnauthenticatedTemplate>
              </Stack>
              <StackItem basis="12px" grow={0} shrink={0}>
                <div />
              </StackItem>
            </Stack>
          </div>
        </ChatManagerContextProvider>
      </DailyPlannerContextProvider>
    </FluentThemeProvider>
  );
};
